import { createReducer } from '@reduxjs/toolkit';
import { State } from 'state';
import { remove, uniq } from 'lodash-es';
import { IModerationUserState, User } from '../types';
import { TModerationUser } from './../../../users/state/types';
import * as actions from '../actions';
import { assignRoleSuccess, unassignRoleSuccess } from '../../../../settings/state/actions';
import { addUserSuccess, fetchUsersSuccess, removeUserSuccess } from '../../../users/state/actions';
import { fetchLiveBlogEvent } from 'app/scripts/components/features/live-blog-v2/state/actions';

const { getSpotItemsSuccess, fetchMessageAuditLogSuccess } = actions;

const initialState: IModerationUserState = {
  users: {}
};

const usersReducer = createReducer(initialState, {
  ['SPOTS/CHANGE_SPOT']: state => {
    return initialState;
  },
  [getSpotItemsSuccess.type]: (state, { payload: { users } }) => {
    setUsers(state, users);
  },
  [fetchMessageAuditLogSuccess.type]: (state, { payload: { users } }) => {
    setUsers(state, users);
  },
  [fetchUsersSuccess.type]: (state, { payload: { users } }) => {
    setUsers(state, users);
  },
  [fetchLiveBlogEvent.fulfilled.type]: (state, { payload: { reporters } }) => {
    setUsers(state, reporters);
  },
  [addUserSuccess.type]: (state, { payload: { userId, ip, user, usersFromIp = [], type } }) => {
    let usersToUpdate = [];
    const actionType = getActionTypeFromUserType(type);

    if (user) {
      setUser(state, user);
    }

    if (!actionType) {
      return;
    }

    if (userId) {
      // Updates the message with the userId if exists
      usersToUpdate.push(state.users[userId]);
    }

    if (ip) {
      // Updates all users related to the IP in the list
      usersToUpdate.push(...usersFromIp.map(userId => state.users[userId]));
    }

    usersToUpdate.forEach((user: User) => {
      const id = user?.get('id');

      if (id) {
        state.users[id] = user.set(actionType, true) as User;
      }
    });
  },
  [removeUserSuccess.type]: (state, { payload: { type, userId } }) => {
    const user = state.users[userId];
    const actionType = getActionTypeFromUserType(type);

    if (user?.get('id')) {
      state.users[userId] = user.set(actionType, false) as User;
    }
  },
  [assignRoleSuccess.type]: (state, { payload: { user, userId, roles = [] } }) => {
    // User entity can also be provided through autocompletion (via find-user)
    const userToAssign = state.users[userId] || user;

    if (!userToAssign || roles.length < 1) {
      console.error(`Unable to assign roles ${roles.join(', ')} for: ${userId}`);
      return;
    }

    const updatedRoles = uniq([...userToAssign.get('roles', []), ...roles]);
    const updatedUser = userToAssign.set('roles', updatedRoles) as User;

    state.users[userId] = updatedUser.set('type', updatedUser.getUserRole()) as User;
  },
  [unassignRoleSuccess.type]: (state, { payload: { userId, roles = [] } }) => {
    const userToAssign = state.users[userId];

    if (!userToAssign || roles.length < 1) {
      console.error(`Unable to assign roles ${roles.join(', ')} for: ${userId}`);
      return;
    }

    const updatedRoles = remove(userToAssign.get('roles', []), role => !roles.includes(role));
    const updatedUser = userToAssign.set('roles', updatedRoles) as User;

    state.users[userId] = updatedUser.set('type', updatedUser.getUserRole()) as User;
  }
});

function setUsers(state, users) {
  users.forEach(user => setUser(state, user));
}

function setUser(state, user: Partial<User> = {}) {
  const id = user?.id;

  if (!id) {
    console.error('setUser - id is required', user);
    return;
  }

  let userModel = state.users?.id;

  if (!userModel) {
    userModel = new User();
  }

  state.users = { ...state.users, [id]: userModel.parse(user) };
}

function getActionTypeFromUserType(type: TModerationUser) {
  const isBlacklisted =
    type === TModerationUser.BlockedUsers || type === TModerationUser.BlockedIps;
  const isWhitelisted =
    type === TModerationUser.AllowedUsers || type === TModerationUser.AllowedIps;
  return isBlacklisted ? 'isBlacklisted' : isWhitelisted ? 'isWhitelisted' : null;
}

// ---------- State Selectors ---------- //

export function getUser(state: State, userId: string): User {
  return state?.moderation?.users?.[userId] || new User();
}

export { usersReducer };
